import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { CloudUpload, GetApp } from '@mui/icons-material';
import { FC, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { frsApi } from 'src/api/frs';
import { GenericModal } from 'src/common/modal/modal';
import { UploadSingleFile } from 'src/common/upload/upload-single-file';
import { FrsBulkImportResult } from './frsTypes';
import {
  FRS_BULK_ACCEPT,
  hasBulkImportChanges,
  triggerBrowserDownload,
  validateExcelFile,
} from './frsUtils';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls'];

interface FrsBulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onImportSuccess: () => void;
}

const SummaryItem: FC<{ label: string; value: number }> = ({ label, value }) => (
  <Typography variant="body2" style={{ fontSize: '0.75rem' }}>
    <strong>{label}:</strong> {value}
  </Typography>
);

const FrsBulkUploadModal: FC<FrsBulkUploadModalProps> = ({
  open,
  onClose,
  projectId,
  onImportSuccess,
}) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<FrsBulkImportResult | null>(null);

  const resetAndClose = useCallback(() => {
    setSelectedFile(null);
    setImportResult(null);
    onClose();
  }, [onClose]);

  const handleDownloadTemplate = async () => {
    if (!projectId) return;
    try {
      setIsDownloadingTemplate(true);
      const { blob, filename } = await frsApi.downloadBulkImportTemplate(projectId);
      triggerBrowserDownload(blob, filename);
      toast.success('Template downloaded');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download template');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleImport = async () => {
    if (!projectId || !selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    try {
      setIsImporting(true);
      setImportResult(null);
      const result = await frsApi.bulkImport(projectId, selectedFile);
      setImportResult(result);

      if (hasBulkImportChanges(result)) {
        onImportSuccess();
      }

      if (result.errors.length === 0) {
        toast.success('FRS bulk import completed successfully');
        setSelectedFile(null);
        onClose();
        setImportResult(null);
        return;
      }

      if (hasBulkImportChanges(result)) {
        toast.error(
          `Import finished with ${result.errors.length} error(s). Review the details below.`
        );
      } else {
        toast.error('Import failed. Fix the errors below and try again.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Bulk import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const showUploadForm = !importResult;

  return (
    <GenericModal
      open={open}
      onClose={resetAndClose}
      title="Bulk Upload FRS"
      width={importResult ? 520 : 480}>
      <Box display="flex" flexDirection="column" gap={2} pb={1}>
        {showUploadForm ? (
          <>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
              Download the template, fill in your FRS data, then upload the completed file.
            </Typography>

            <Button
              variant="outlined"
              color="primary"
              startIcon={
                isDownloadingTemplate ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <GetApp />
                )
              }
              onClick={handleDownloadTemplate}
              disabled={!projectId || isDownloadingTemplate}
              style={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              Download template
            </Button>

            <Box>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontSize: '0.75rem', marginBottom: 8 }}>
                Upload file ({ACCEPTED_EXTENSIONS.join(', ')})
              </Typography>
              <UploadSingleFile
                file={selectedFile}
                documentMode
                onChange={(file: File) => {
                  setSelectedFile(file || null);
                  setImportResult(null);
                }}
                accept={FRS_BULK_ACCEPT}
                validator={validateExcelFile}
              />
              {selectedFile && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
              Import summary
            </Typography>
            <Box
              p={1.5}
              borderRadius={4}
              bgcolor={theme.palette.background.default}
              border={`1px solid ${theme.palette.divider}`}>
              <SummaryItem label="Categories created" value={importResult.categoriesCreated} />
              <SummaryItem label="Categories updated" value={importResult.categoriesUpdated} />
              <SummaryItem label="FRS created" value={importResult.frsCreated} />
              <SummaryItem label="FRS updated" value={importResult.frsUpdated} />
            </Box>

            {importResult.errors.length > 0 && (
              <Box
                p={1.5}
                borderRadius={4}
                maxHeight={220}
                style={{
                  overflowY: 'auto',
                  backgroundColor: theme.palette.feedback.error.background,
                  border: `1px solid ${theme.palette.feedback.error.border}`,
                }}>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: theme.palette.feedback.error.title,
                    marginBottom: 8,
                  }}>
                  {importResult.errors.length} row error
                  {importResult.errors.length === 1 ? '' : 's'} (fix in Excel and re-upload)
                </Typography>
                {importResult.errors.map((item) => (
                  <Typography
                    key={`${item.rowNumber}-${item.message}`}
                    variant="body2"
                    style={{
                      fontSize: '0.75rem',
                      color: theme.palette.feedback.error.text,
                      marginBottom: 4,
                    }}>
                    <strong>Row {item.rowNumber}:</strong> {item.message}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}

        <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={resetAndClose}
            disabled={isImporting}
            style={{ textTransform: 'none' }}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {showUploadForm ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isImporting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CloudUpload />
                )
              }
              onClick={handleImport}
              disabled={!selectedFile || isImporting}
              style={{ textTransform: 'none' }}>
              {isImporting ? 'Uploading...' : 'Upload & import'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setImportResult(null);
                setSelectedFile(null);
              }}
              style={{ textTransform: 'none' }}>
              Upload another file
            </Button>
          )}
        </Box>
      </Box>
    </GenericModal>
  );
};

export default FrsBulkUploadModal;
